<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="450"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          class="pl-1"
          flat
          height="80"
          max-height="80"
        >
          <v-avatar color="primary">
            <v-icon dark>
              mdi-credit-card-wireless
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Settings.Payment.AddTerminal.Toolbar.Title') }}
            </div>

            <div class="text-caption">
              {{ $t('Settings.Payment.AddTerminal.Toolbar.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-1">
          <v-form
            ref="form"
            v-model="formValid"
            @submit.prevent
          >
            <v-container fluid>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="model.Nickname"
                    :rules="[v => !!v || $t('Common.Error.Required')]"
                    clearable
                    filled
                    hide-details="auto"
                    label="Nickname"
                    type="text"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="model.AuthCode"
                    :disabled="loading"
                    :rules="[v => !!v || $t('Common.Error.Required')]"
                    clearable
                    filled
                    hide-details="auto"
                    label="Redeem Code"
                    type="text"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="model.TerminalId"
                    :rules="[v => !!v || $t('Common.Error.Required')]"
                    clearable
                    filled
                    hide-details="auto"
                    label="Terminal Id"
                    type="text"
                  />
                </v-col>

                <v-col cols="12">
                  <v-select
                    v-model="model.AcquirerCode"
                    :disabled="loading"
                    :items="$t('Settings.Payment.Terminal.Vendor.Edps.Acquirer')"
                    :rules="[v => !!v || $t('Common.Error.Required')]"
                    clearable
                    filled
                    hide-details="auto"
                    item-value="Value"
                    label="Acquirer"
                  >
                    <template #selection="{item}">
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.Name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ item.SubAcquirer }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>

                    <template #item="{item}">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.Name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.SubAcquirer }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12">
                  <v-select
                    v-model="model.ModelId"
                    :disabled="loading || !model.AcquirerCode"
                    :items="terminalsList"
                    :rules="[v => !!v || $t('Common.Error.Required')]"
                    clearable
                    filled
                    hide-details="auto"
                    item-value="Id"
                    label="Device / Terminal"
                  >
                    <template #selection="{item}">
                      <v-list-item class="px-0">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.Manufacturer }}</v-list-item-title>
                          <v-list-item-subtitle>{{ item.Model }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>

                    <template #item="{item}">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.Manufacturer }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.Model }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>

                  <div v-if="vendor && model.ModelId">
                    <v-btn
                      :href="getTerminalErpGuideUrl(vendor.Id, model.ModelId)"
                      block
                      class="mt-4"
                      color="info"
                      style="text-transform: none;"
                      target="_blank"
                      text
                    >
                      <v-icon left>
                        mdi-information-variant-circle-outline
                      </v-icon>
                      {{ $t('Settings.Payment.AddTerminal.TerminalGuide') }}
                    </v-btn>
                  </div>
                </v-col>

                <v-col
                  v-if="!!serverError"
                  cols="12"
                >
                  <v-alert
                    border="left"
                    class="text-caption py-1 px-3 ma-0"
                    dense
                    text
                    type="error"
                  >
                    {{ serverError }}
                  </v-alert>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
          <v-btn
            :disabled="loading"
            :loading="loading"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import TerminalsCommon from '@/mixins/payments/terminalsCommon'

export default {
  name      : 'EdpsTerminalAddDialog',
  components: {},
  mixins    : [AppData, Auth, TerminalsCommon],
  props     : {
    vendor: {
      type   : Object,
      default: undefined
    },
    visible: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {
      serverError: '',
      loading    : false,
      formValid  : false,
      model      : {}
    }
  },
  computed: {
    terminalsList() {
      const data = this.getVendorAcquirerTerminals(this.vendor?.Id, this.model?.AcquirerCode)
      //console.log(data.map(o => o.Manufacturer + ' ' + o.Model).join(', '))
      return data
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    'model.ModelId': {
      deep     : false,
      immediate: true,
      handler(newVal) {
        const modelId = newVal
        const devices = this.$te('Settings.Payment.Terminal.Vendor.Edps.Devices') ? this.$t('Settings.Payment.Terminal.Vendor.Edps.Devices') : []
        const device = devices.find(device => device.Id === modelId)
        this.model.Model = device?.Model || ''
        this.model.Manufactor = device?.Manufacturer || ''
        this.model.Connection = device?.CommunicationType || ''
        this.model.OperationSystem = device?.OperatingSystem || ''
      }
    },
    'model.AcquirerCode': function () {
      this.model.ModelId = ''
    },

    visible(newVal) {
      if (newVal) {
        this.loading = false
        this.serverError = ''
        const acquirers = this.$t('Settings.Payment.Terminal.Vendor.Edps.Acquirer')
        const acquirer = acquirers.length === 1 ? acquirers[0].Value : ''
        this.model = {
          Id  : '',
          Code: '',

          AuthCode  : '',
          TerminalId: '',
          Nickname  : '',

          AcquirerCode   : acquirer || '',
          ModelId        : '',
          Model          : '',
          Manufactor     : '',
          Connection     : '',
          OperationSystem: ''
        }

        this.$bus.$on(window.SocketCommand.Settings.Payments.Terminal.Add.WebEcr, this.onAddTerminalResult)
      } else {
        this.$bus.$off(window.SocketCommand.Settings.Payments.Terminal.Add.WebEcr, this.onAddTerminalResult)
      }
    }
  },
  created() {
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    onSaveButtonClick() {
      this.serverError = ''
      this.$refs.form.validate()

      if (this.formValid && !this.loading) {
        this.loading = true
        this.addTerminal()
      }
    },

    addTerminal() {
      this.model.Id = this.vendor.Id
      this.model.Code = this.vendor.Code

      window.callAS(window.SocketCommand.Settings.Payments.Terminal.Add.WebEcr, this.model)
    },
    onAddTerminalResult(result) {
      if (result.status === 'success') {
        this.$emit('terminal-added', this.model)
        this.onCancelButtonClick()
      } else {
        this.serverError = result.message || this.$t('Common.Error.Generic')
      }
      this.loading = false
    },

    onCancelButtonClick() {
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.isVisible = false
      this.loading = false
      this.serverError = ''
    }
  }
}
</script>
